import React, { useRef, useEffect, MutableRefObject } from 'react';
import { PitchResult } from '../utils/pitchDetection';
import { chroma, guessChord, NOTE_NAMES } from '../utils/timbre';

interface Props {
  /** dB spectrum; bin count is taken from the array length so a high resolution analyser can be passed. */
  spectrumRef: MutableRefObject<Float32Array | null>;
  pitchRef: MutableRefObject<PitchResult | null>;
  sampleRate: number;
}

const SMOOTH = 0.35;        // EMA toward the new chroma frame (higher = twitchier)
const LABEL_HOLD_MS = 700;  // keep the last chord name up briefly so it does not flicker

// Twelve pitch-class bars (C..B) from the live spectrum, plus a chord guess
// when more than one class is clearly ringing. This is what a strummed
// chord looks like; single plucked notes show up as one tall bar.
export default function NotesRinging({ spectrumRef, pitchRef, sampleRate }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const smoothRef = useRef<Float32Array>(new Float32Array(12));
  const labelRef = useRef<{ text: string; sub: string; until: number }>({ text: '', sub: '', until: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let animId: number;

    const draw = (ts: number) => {
      const dpr = window.devicePixelRatio || 1;
      const cw = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const ch = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
      const W = cw, H = ch;
      ctx.clearRect(0, 0, W, H);

      const spectrum = spectrumRef.current;
      const pitch = pitchRef.current;
      const sm = smoothRef.current;

      if (spectrum) {
        const c = chroma(spectrum, sampleRate, spectrum.length);
        for (let i = 0; i < 12; i++) sm[i] += (c[i] - sm[i]) * SMOOTH;
      } else {
        for (let i = 0; i < 12; i++) sm[i] *= 0.9;
      }

      const padL = 6 * dpr, padR = 6 * dpr, padT = 22 * dpr, padB = 14 * dpr;
      const plotH = H - padT - padB;
      const slot = (W - padL - padR) / 12;
      const barW = slot * 0.7;

      const pitchPc = pitch ? ((Math.round(12 * Math.log2(pitch.hz / 440) + 69) % 12) + 12) % 12 : -1;

      // Baseline
      ctx.strokeStyle = 'rgba(69,124,159,0.6)';
      ctx.lineWidth = 1 * dpr;
      ctx.beginPath(); ctx.moveTo(padL, H - padB + 0.5); ctx.lineTo(W - padR, H - padB + 0.5); ctx.stroke();

      ctx.font = `${9 * dpr}px "Courier New", monospace`;
      ctx.textAlign = 'center';
      for (let pc = 0; pc < 12; pc++) {
        const v = sm[pc];
        const x = padL + pc * slot + (slot - barW) / 2;
        const bh = v * plotH;
        const y = H - padB - bh;
        const strong = v >= 0.5;
        if (bh > 0.5) {
          const grad = ctx.createLinearGradient(0, y, 0, H - padB);
          if (strong) { grad.addColorStop(0, 'rgba(208,224,122,0.95)'); grad.addColorStop(1, 'rgba(152,165,82,0.5)'); }
          else { grad.addColorStop(0, 'rgba(127,184,221,0.55)'); grad.addColorStop(1, 'rgba(69,124,159,0.25)'); }
          ctx.fillStyle = grad;
          ctx.fillRect(x, y, barW, bh);
        }
        if (pc === pitchPc) {
          // Outline the class the pitch tracker is locked on
          ctx.strokeStyle = 'rgba(227,185,85,0.9)';
          ctx.lineWidth = 1.5 * dpr;
          ctx.strokeRect(x - 1 * dpr, padT, barW + 2 * dpr, plotH);
        }
        const isSharp = NOTE_NAMES[pc].length > 1;
        ctx.fillStyle = strong ? 'rgba(250,237,175,0.95)' : isSharp ? 'rgba(122,109,140,0.7)' : 'rgba(182,173,196,0.8)';
        ctx.fillText(isSharp ? NOTE_NAMES[pc] : NOTE_NAMES[pc], x + barW / 2, H - 3 * dpr);
      }

      // Chord / note label with hold
      const guess = guessChord(sm);
      const lbl = labelRef.current;
      if (guess) {
        lbl.text = guess.label;
        lbl.sub = guess.notes.join(' · ');
        lbl.until = ts + LABEL_HOLD_MS;
      } else if (pitch && pitch.clarity > 0.6) {
        lbl.text = `${pitch.note}${pitch.octave}`;
        lbl.sub = 'single note';
        lbl.until = ts + LABEL_HOLD_MS / 2;
      }
      ctx.textAlign = 'left';
      if (ts < lbl.until && lbl.text) {
        ctx.font = `bold ${14 * dpr}px "Courier New", monospace`;
        ctx.fillStyle = 'rgba(250,237,175,0.95)';
        ctx.fillText(lbl.text, padL, 15 * dpr);
        const tw = ctx.measureText(lbl.text).width;
        ctx.font = `${9 * dpr}px "Courier New", monospace`;
        ctx.fillStyle = 'rgba(182,173,196,0.8)';
        ctx.fillText(lbl.sub, padL + tw + 8 * dpr, 15 * dpr);
      } else {
        ctx.font = `bold ${10 * dpr}px "Courier New", monospace`;
        ctx.fillStyle = 'rgba(122,109,140,0.85)';
        ctx.fillText('strum or pluck', padL, 14 * dpr);
      }

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [spectrumRef, pitchRef, sampleRate]);

  return (
    <div className="rail-canvas-wrap">
      <canvas ref={canvasRef} className="rail-canvas" />
    </div>
  );
}
